import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router';

import { defaultAllowedRoles, manager, teamAdmin, teamMember } from '../../constants/appConstants';
import PageLoader from '../loaders/pageLoader';
import { rootLoader } from '../loaders/rootLoader';
import { walletLoader } from '../loaders/wallet';
import { walletDetailsLoader } from '../loaders/walletDetails';
import ProtectedRoute from '../protectedRoute';
import { queryClient } from '../queryClient';

const WalletPage = lazy(() => import('../../components/pages/wallet'));
const WalletDetails = lazy(() => import('../../components/pages/wallet/walletDetails'));

const walletPath = '/wallet' as const;

export const walletRoutes = {
	path: walletPath,
	element: <Outlet />,

	children: [
		{
			index: true,
			element: (
				<ProtectedRoute allowedRoles={defaultAllowedRoles}>
					<Suspense fallback={<PageLoader />}>
						<WalletPage />
					</Suspense>
				</ProtectedRoute>
			),
			loader: async (ctx: any) => {
				const [rootData, walletData] = await Promise.all([
					rootLoader({ queryClient, ...ctx }),
					walletLoader({ queryClient, ...ctx }),
				]);
				return {
					...rootData,
					...walletData,
				};
			},
		},
		{
			path: 'my-cards',
			element: (
				<ProtectedRoute allowedRoles={[teamAdmin, manager, teamMember]}>
					<Suspense fallback={<PageLoader />}>
						<WalletPage />
					</Suspense>
				</ProtectedRoute>
			),
			loader: async (ctx: any) => {
				const [rootData, walletData] = await Promise.all([
					rootLoader({ queryClient, ...ctx }),
					walletLoader({ queryClient, ...ctx }),
				]);
				return {
					...rootData,
					...walletData,
				};
			},
		},
		{
			path: 'team',
			element: (
				<ProtectedRoute allowedRoles={[teamAdmin, teamMember]}>
					<Suspense fallback={<PageLoader />}>
						<WalletPage />
					</Suspense>
				</ProtectedRoute>
			),
			loader: async (ctx: any) => {
				const [rootData, walletData] = await Promise.all([
					rootLoader({ queryClient, ...ctx }),
					walletLoader({ queryClient, ...ctx }),
				]);
				return {
					...rootData,
					...walletData,
				};
			},
		},
		{
			path: 'team/:teamId/user/:userId',
			element: (
				<ProtectedRoute allowedRoles={[...defaultAllowedRoles, teamAdmin, manager]}>
					<Suspense fallback={<PageLoader />}>
						<WalletPage />
					</Suspense>
				</ProtectedRoute>
			),
			loader: async (ctx: any) => {
				const [rootData, walletData] = await Promise.all([
					rootLoader({ queryClient, ...ctx }),
					walletLoader({ queryClient, ...ctx }),
				]);
				return {
					...rootData,
					...walletData,
				};
			},
		},
		{
			path: ':taskId',
			element: (
				<ProtectedRoute allowedRoles={[...defaultAllowedRoles, teamAdmin, teamMember, manager]}>
					<Suspense fallback={<PageLoader />}>
						<WalletDetails />
					</Suspense>
				</ProtectedRoute>
			),
			loader: async (ctx: any) => {
				const [rootData, walletData] = await Promise.all([
					rootLoader({ queryClient, ...ctx }),
					walletDetailsLoader({ queryClient, ...ctx }),
				]);
				return {
					...rootData,
					...walletData,
				};
			},
		},
		{
			path: 'card/:virtualCardId',
			element: (
				<ProtectedRoute allowedRoles={[...defaultAllowedRoles, teamAdmin, manager]}>
					<Suspense fallback={<PageLoader />}>
						<WalletDetails />
					</Suspense>
				</ProtectedRoute>
			),
			loader: async (ctx: any) => {
				const [rootData, walletData] = await Promise.all([
					rootLoader({ queryClient, ...ctx }),
					walletDetailsLoader({ queryClient, ...ctx }),
				]);
				return {
					...rootData,
					...walletData,
				};
			},
		},
	],
};
